import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './Toast';
import './PaymentMethods.css';

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'stripe' | 'mbway' | 'bizum' | 'paypal' | 'other';
  description: string;
  icon: string;
  enabled: boolean;
  config?: Record<string, any>;
  paymentUrl?: string;
}

interface PaymentMethodsProps {
  paymentMethods: PaymentMethod[];
  totalAmount: number;
  currency: string;
  onPaymentMethodSelect: (method: PaymentMethod) => void;
  onCancel: () => void;
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  paymentMethods,
  totalAmount,
  currency,
  onPaymentMethodSelect,
  onCancel
}) => {
  const { t } = useTranslation();
  const { toasts, removeToast, showError } = useToast();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [processing, setProcessing] = useState(false);

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const getPaymentMethodIcon = (method: PaymentMethod) => {
    // If method has a custom icon (image URL), use it
    if (method.icon && method.icon.startsWith('http')) {
      return method.icon;
    }
    
    // Fallback to default emojis for backward compatibility
    switch (method.type) {
      case 'stripe':
        return '💳';
      case 'mbway':
        return '📱';
      case 'bizum':
        return '💰';
      case 'paypal':
        return '🔵';
      default:
        return method.icon || '💳';
    }
  };

  const getPaymentMethodDescription = (method: PaymentMethod) => {
    switch (method.type) {
      case 'stripe':
        return t('payment.cardDescription');
      case 'mbway':
        return t('payment.mbwayDescription');
      case 'bizum':
        return t('payment.bizumDescription');
      case 'paypal':
        return t('payment.paypalDescription');
      default:
        return method.description || t('payment.genericDescription');
    }
  };

  const handleMethodSelect = (method: PaymentMethod) => {
    setSelectedMethod(method);
  };

  const handleProceedWithPayment = async () => {
    if (!selectedMethod) {
      showError(t('payment.selectMethodRequired'));
      return;
    }

    setProcessing(true);
    
    try {
      // Store current event ID for potential retry
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing
      onPaymentMethodSelect(selectedMethod);
    } catch (error) {
      showError(t('payment.processingError'));
    } finally {
      setProcessing(false);
    }
  };

  const enabledMethods = paymentMethods.filter(method => method.enabled);

  if (enabledMethods.length === 0) {
    return (
      <div className="payment-methods no-methods">
        <div className="no-methods-container">
          <div className="error-icon">❌</div>
          <h3>{t('payment.noMethodsAvailable')}</h3>
          <p>{t('payment.contactSupport')}</p>
          <button onClick={onCancel} className="btn-secondary">
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-methods">
      <div className="payment-container">
        {/* Header */}
        <div className="payment-header">
          <h2>{t('payment.selectMethod')}</h2>
          <div className="total-amount">
            <span className="amount-label">{t('payment.totalToPay')}:</span>
            <span className="amount-value">{formatPrice(totalAmount, currency)}</span>
          </div>
        </div>

        {/* Payment Methods List */}
        <div className="methods-list">
          <h3>{t('payment.availableMethods')}</h3>
          
          {enabledMethods.map((method) => (
            <div 
              key={method.id}
              className={`payment-method-card ${selectedMethod?.id === method.id ? 'selected' : ''}`}
              onClick={() => handleMethodSelect(method)}
            >
              <div className="method-icon">
                {getPaymentMethodIcon(method).startsWith('http') ? (
                  <img 
                    src={getPaymentMethodIcon(method)} 
                    alt={method.name}
                    className="payment-icon-image"
                  />
                ) : (
                  getPaymentMethodIcon(method)
                )}
              </div>
              
              <div className="method-content">
                <div className="method-header">
                  <h4 className="method-name">{method.name}</h4>
                  {method.type === 'stripe' && (
                    <div className="accepted-cards">
                      <span className="card-icon">💳</span>
                      <span className="card-icon">🔷</span>
                      <span className="card-icon">🟡</span>
                    </div>
                  )}
                </div>
                
                <p className="method-description">
                  {getPaymentMethodDescription(method)}
                </p>
                
                {method.type === 'mbway' && (
                  <div className="method-features">
                    <span className="feature">✅ {t('payment.instantPayment')}</span>
                    <span className="feature">🔒 {t('payment.securePayment')}</span>
                  </div>
                )}
                
                {method.type === 'bizum' && (
                  <div className="method-features">
                    <span className="feature">⚡ {t('payment.fastPayment')}</span>
                    <span className="feature">🏦 {t('payment.bankGuarantee')}</span>
                  </div>
                )}
              </div>
              
              <div className="method-selector">
                <div className={`radio-button ${selectedMethod?.id === method.id ? 'selected' : ''}`}>
                  {selectedMethod?.id === method.id && <div className="radio-inner"></div>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Security Information */}
        <div className="security-info">
          <div className="security-badge">
            <span className="security-icon">🔒</span>
            <div className="security-content">
              <h4>{t('payment.secureTransaction')}</h4>
              <p>{t('payment.securityDescription')}</p>
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="payment-terms">
          <div className="terms-content">
            <p>{t('payment.termsText')}</p>
            <div className="terms-links">
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                {t('payment.termsConditions')}
              </a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                {t('payment.privacyPolicy')}
              </a>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="payment-actions">
          <button 
            onClick={onCancel} 
            className="btn-secondary"
            disabled={processing}
          >
            {t('common.cancel')}
          </button>
          
          <button 
            onClick={handleProceedWithPayment}
            disabled={!selectedMethod || processing}
            className="btn-primary"
          >
            {processing ? (
              <>
                <span className="spinner"></span>
                {t('payment.processing')}
              </>
            ) : (
              selectedMethod?.type === 'stripe' 
                ? t('payment.proceedToCard')
                : t('payment.proceedToPayment')
            )}
          </button>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer messages={toasts} onClose={removeToast} />
    </div>
  );
};

export default PaymentMethods;